import { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import classNames from "classnames";
import { ITreeData } from "../Hierarchy/Tree";
import { getOrganizationTrees } from "../../../core/api/organizations/organizations";
import { transformOrganizationsToTreeData } from "../../../core/utils/organizations";
import { notifyApiErrors } from "../../../core/helpers/helpers";
import useTranslations from "../../../core/i18n/useTranslations";
import PrimeModal from "../PrimeModal/PrimeModal";
import useUser from "../../../core/user/useUser";
import LoadingSpinner from "../Loading/LoadingSpinner";

import styles from "./ChildrenTreePopup.module.css";

const ChildrenTreeModal = ({
  organizationId,
  isOpen,
  onClose = () => {},
}: {
  organizationId?: number;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const t = useTranslations();
  const { authenticatedRequest } = useUser();
  const [isLoading, setIsLoading] = useState(true);
  const [branchData, setBranchData] = useState<ITreeData[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchOrganizationsTree = async () => {
    try {
      setIsLoading(true);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const response = await getOrganizationTrees(
        {
          parentId: organizationId,
          includeParent: true,
          getRoot: true,
        },
        authenticatedRequest,
        abortControllerRef.current.signal
      );
      setBranchData(
        transformOrganizationsToTreeData(response.data.organizations)
      );
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    } finally {
      setIsLoading(false);
    }
  };

  const renderChildren = (child: ITreeData, step = 1) => (
    <div key={`${child.key}_key`}>
      <Link to={`/adminportal/organizations/${child.key}`}>
        <p
          className={
            child.key === organizationId
              ? styles.popupRootChild
              : styles.popupSecondaryChild
          }
          key={child.key}
          style={{ paddingLeft: `${12 * step}px` }}
        >
          {child.label}
        </p>
      </Link>
      {child?.children?.length
        ? child.children.map((innerChild) =>
            renderChildren(innerChild, step + 1)
          )
        : null}
    </div>
  );

  useEffect(() => {
    if (isOpen && organizationId) {
      fetchOrganizationsTree();
    }
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [organizationId, isOpen]);

  if (!organizationId) return null;

  return (
    <PrimeModal
      withHeader
      withFooter
      isOpen={isOpen}
      onClose={onClose}
      header={t("Organizations:organizations_hierarchy")}
      submitBtn={{
        text: t("Common:close"),
        variant: "secondary",
        onClick: onClose,
      }}
      fixedWidth="848px"
    >
      <div>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            {branchData.length > 0 &&
              branchData.map((org) => (
                <Fragment key={org.key}>
                  <Link to={`/adminportal/organizations/${org.key}`}>
                    <p
                      className={classNames({
                        [styles.popupRoot]: org.key !== organizationId,
                        [styles.popupMainChild]: org.key === organizationId,
                      })}
                    >
                      {org.label}
                    </p>
                  </Link>
                  {org.children?.map((child: ITreeData) =>
                    renderChildren(child)
                  )}
                </Fragment>
              ))}
          </>
        )}
      </div>
    </PrimeModal>
  );
};

export default ChildrenTreeModal;
